import { nlpApi } from '@tunasong/redux'
import type { AgentStreamPayload, ChatMessageStream, ToolCallSummary } from '@tunasong/redux'

/**
 * Projection of an agent's latest turn. The sidebar consumer renders the streamed content as a synthetic feed
 * item below the persisted comments while `pending: true`, and keeps rendering it until either the persisted
 * comment arrives (replacing the item) or the cache entry evicts.
 */
export interface AgentTurnStream {
  /** RTK Query cache key — used as the React `key` of the synthetic item. */
  requestId: string
  /** Per-turn id — the cancel/continuation key. */
  turnId?: string
  /** The user message that started the turn — used by Retry. */
  message?: string
  /** Accumulated text content (from `text-delta` chunks). */
  content: string
  /** Tool calls projected from the parts stream, in invocation order. */
  toolCalls: ToolCallSummary[]
  /** True while the stream is still arriving. */
  pending: boolean
  /** Terminal error message, if the fetch failed. */
  error?: string
}

interface QueryEntry {
  endpointName?: string
  originalArgs?: AgentStreamPayload
  startedTimeStamp?: number
  data?: ChatMessageStream | null
}

const isAgentStreamEntry = (entry: unknown): entry is QueryEntry => {
  if (typeof entry !== 'object' || entry === null) {
    return false
  }
  const candidate = entry as QueryEntry
  return candidate.endpointName === 'agentStream' && typeof candidate.originalArgs?.agentId === 'string'
}

/**
 * The agent's most recent turn in the `agentStream` RTK Query cache, or `null`. A pure function of state, so
 * a hook (`useAgentTurnStream`, through `useSelector`) and a command (through `getState`) share one lookup.
 *
 * Lookup is by iteration rather than by key because the cache key includes `requestId` (a per-submit nonce we
 * don't have at read time). Iterating the few entries is well below any perf budget.
 */
export const selectLatestAgentTurn = (state: unknown, agentId: string | undefined): AgentTurnStream | null => {
  if (!agentId) {
    return null
  }
  // The structural cast keeps this portable for tests that mount a smaller store.
  const slice = (state as Record<string, unknown>)[nlpApi.reducerPath] as
    { queries?: Record<string, unknown> } | undefined
  const queries = slice?.queries
  if (!queries) {
    return null
  }

  let best: QueryEntry | null = null
  for (const value of Object.values(queries)) {
    if (!isAgentStreamEntry(value) || value.originalArgs?.agentId !== agentId) {
      continue
    }
    if (best === null || (value.startedTimeStamp ?? 0) > (best.startedTimeStamp ?? 0)) {
      best = value
    }
  }

  if (!best || !best.originalArgs) {
    return null
  }

  const data = best.data ?? null
  return {
    requestId: best.originalArgs.requestId,
    turnId: best.originalArgs.turnId,
    message: best.originalArgs.message,
    content: data?.content ?? '',
    toolCalls: data?.toolCalls ?? [],
    pending: data?.pending ?? true,
    error: data?.error,
  } satisfies AgentTurnStream
}

/** Whether the agent is still answering: its latest turn is in flight and hasn't failed. */
export const isAgentAnswering = (state: unknown, agentId: string | undefined): boolean => {
  const turn = selectLatestAgentTurn(state, agentId)
  return Boolean(turn && turn.pending && !turn.error)
}
